import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchCategorias, removeCategoria } from '../../reducers/categoria_slice';
import Table from 'react-bootstrap/Table';
import CustomPrintModal from '../UI/CustomPrintModal';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import ActionButton from '../UI/Buttons/ActionButton';
import FormCategoria from './FormCategoria';
import ModalIndicesCalculo from './ModalIndicesCalculo';

const ListCategorias = () => {
  const dispatch = useDispatch();
  const categoriaState = useSelector(state => state.categoria || { categorias: [] });
  const categorias = categoriaState.categorias || [];
  const [showModal, setShowModal] = useState(false);
  const [showModalIndices, setShowModalIndices] = useState(false);
  const [currentCategoria, setCurrentCategoria] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [printContent, setPrintContent] = useState({ head: [], body: [] });
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(fetchCategorias());
  }, [dispatch]);

  const handleDelete = id => {
    Swal.fire({
      title: '¿Estás seguro?',
      text: "¡No podrás revertir esto!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#464646',
      confirmButtonText: 'Sí, eliminarlo!',
       cancelButtonText: 'Cancelar',
      reverseButtons: true
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(removeCategoria(id)).unwrap().then(() => {
          Swal.fire('¡Eliminado!', 'La categoría ha sido eliminada.', 'success');
        }).catch((error) => {
          Swal.fire('Error', error.error || 'Ocurrió un error al eliminar la categoría', 'error');
        });
      }
    });
  };

  const handleSort = (key) => {
    if(sortConfig.direction === 'desc'){
        setSortConfig({ key: null, direction: 'asc' });
        return;
    }
    
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    } else if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = null;
    }
    setSortConfig({ key, direction });
  };

  const handleEdit = categoria => {
    setCurrentCategoria(categoria);
    setShowModal(true);
  };

  const handleCreate = () => {
    setCurrentCategoria(null);
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setCurrentCategoria(null);
  };

  const handleShowIndices = (categoria) => {
    setCurrentCategoria(categoria);
    setShowModalIndices(true);
  };

  const handleCloseModalIndices = () => {
    setShowModalIndices(false);
    setCurrentCategoria(null);
  };
  const handleBack = () => {
    navigate(-1);
  };

  const handleExport = () => {
    const contentArrays = getPreviewContent()
    // Combinar las cabeceras y los datos
    const exportData = [...contentArrays.head, ...contentArrays.body];
    // Crear la hoja de trabajo con las cabeceras personalizadas
    const worksheet = XLSX.utils.aoa_to_sheet(exportData); 
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Categorias");
    XLSX.writeFile(workbook, "categorias.xlsx");
  };

  const sortedCategorias = useMemo(() => {
    let sortedData = [...categorias];
    if (sortConfig.key) {
      sortedData.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortedData;
  }, [categorias, sortConfig]);

  const handleShowPreview = () => {
    setPrintContent(getPreviewContent());
    setShowPreviewModal(true);
  };

  const handleClosePreview = () => {
    setShowPreviewModal(false);
  };

  const getPreviewContent = () => {
    const content = {
      head: [['N°', 'Nombre', 'Costo Mes (CM)']],
      body: sortedCategorias.map((categoria, index) => [
        index+1,
        categoria.nombre,
        categoria.costo_mes,
      ])
    };
    return content;
  };

  return (
    <div className="list-container list3">
      <h1 className='table-title'>Lista de Categorías</h1>
      <div className="list-content">
        <div className="list-container-header">
          <div className="left-buttons">
            <button title="Volver" onClick={handleBack} className="icon-button btn-transparent"><i className="bi bi-arrow-left"></i></button>
          </div>
          <div className="right-buttons">
            <button title="Imprimir" onClick={handleShowPreview} className="icon-button btn-transparent"><i className="bi bi-printer"></i></button>
            <button title="Exportar a excel" onDoubleClick={handleExport} className="icon-button btn-transparent mr-3"><i className="bi bi-file-spreadsheet"></i></button>
            <ActionButton onClick={handleCreate} outline icon='plus' floatEnd>
              Nueva Categoría
            </ActionButton>
          </div>
        </div>
        {categorias.length === 0 ? (
          <p>No hay categorías disponibles.</p>
        ) : (
          <div className='table-container'>
            <Table bordered hover size='sm'>
              <thead className='text-center align-middle'>
                <tr>
                  <th className='col-index'>N°</th>
                  <th  className="text-start" onClick={() => handleSort('nombre')}>
                    Categoría
                    {sortConfig.key === 'nombre' && (
                        sortConfig.direction === 'asc' ? <i className="bi bi-arrow-up"></i> : <i className="bi bi-arrow-down"></i>
                    )}
                  </th>
                  <th onClick={() => handleSort('nombre')}>
                    Costo Mes (Bs)
                    {sortConfig.key === 'nombre' && (
                        sortConfig.direction === 'asc' ? <i className="bi bi-arrow-up"></i> : <i className="bi bi-arrow-down"></i>
                    )}
                  </th>
                  <th className='col-simple-actions' onClick={() => handleSort('num')}>
                    Número de orden
                    {sortConfig.key === 'num' && (
                        sortConfig.direction === 'asc' ? <i className="bi bi-arrow-up"></i> : <i className="bi bi-arrow-down"></i>
                    )}
                  </th>
                  <th className='col-actions4'>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {sortedCategorias.map((categoria, index) => (
                  <tr key={categoria.id} className="text-center">
                    <td>{index + 1}</td>
                    <td className="text-start">{categoria.nombre}</td>
                    <td>{categoria.costo_mes}</td>
                    <td style={{width:"20%"}}>{categoria.num}</td>
                    <td>
                      <button className="icon-button btn-secondary-outline" title="Editar" onClick={() => handleEdit(categoria)}>
                        <i className="bi bi-pencil"></i>
                      </button>
                      <button className="icon-button btn-secondary-outline" title="Eliminar" onClick={() => handleDelete(categoria.id)}>
                        <i className="bi bi-trash"></i>
                      </button>
                      <button className="btn-sm btn-secondary-outline" title="Gestionar índices de cálculo" onClick={() => handleShowIndices(categoria)}>
                          Índices
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
        <FormCategoria currentCategoria={currentCategoria} showModal={showModal} handleClose={handleClose} />
      {currentCategoria && showModalIndices && <ModalIndicesCalculo currentCategoria={currentCategoria}  showModal={showModalIndices} handleClose={handleCloseModalIndices} />}
        <CustomPrintModal
          show={showPreviewModal}
          handleClose={handleClosePreview}
          title1="LISTA DE CATEGORÍAS DE LAS TIPOLOGÍAS DE PROYECTOS"
          title2=""
          content={printContent}
          copia={false}
        />
      </div>
    </div>
  );
};

export default ListCategorias;
